import { ToggleButton } from '@plato/ui/primitives/toggle-button';
import { cn } from '@plato/ui/util/classes';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { ActivityFeedPreset } from '../lib/activity-feed-presets.ts';

export type ActivityFilterPreset = {
    preset: ActivityFeedPreset;
    /** Leads the label once loaded, e.g. "3 open orders". */
    count?: number;
};

export type ActivityFilterPresetsProps = {
    presets: ActivityFilterPreset[];
    active: ActivityFeedPreset | undefined;
    /** The preset to switch on, or undefined when the active one is clicked again. */
    onChange: (preset: ActivityFeedPreset | undefined) => void;
    className?: string;
};

/**
 * Round toggles over the feed that set a pair of filters (open quotes, open
 * orders) in one click and clear them on the next. Scrolls sideways when the
 * row does not fit a narrow phone.
 */
export function ActivityFilterPresets({
    presets,
    active,
    onChange,
    className,
}: ActivityFilterPresetsProps): ReactElement {
    const { t } = useTranslation('activity-feed');

    return (
        <div
            role="group"
            aria-label={t('presets__label')}
            className={cn(
                // the vertical padding keeps the pressed offset from being clipped by the scroll box
                '-my-tight flex gap-cozy overflow-x-auto py-tight scrollbar-none scroll-fade-x [--scroll-fade-width:--spacing(8)]',
                className,
            )}
        >
            {presets.map(({ preset, count }) => {
                const pressed = preset === active;
                const countElement = (
                    <span
                        className={cn(
                            'font-semibold tabular-nums',
                            pressed ? 'text-blue-500' : 'text-content-weaker',
                        )}
                    />
                );

                return (
                    <ToggleButton
                        key={preset}
                        aria-pressed={pressed}
                        checked={pressed}
                        className={cn(
                            'rounded-full bg-container',
                            // the toggle's own wash shows the grey page through; match the
                            // opaque blue of the applied filter pills instead
                            'state-checked-within:border-blue-300 state-checked-within:bg-blue-50 state-checked-within:text-blue-700',
                            'hover:state-checked-within:bg-blue-100',
                        )}
                        onClick={() => {
                            onChange(pressed ? undefined : preset);
                        }}
                    >
                        {count === undefined ? (
                            <>
                                {preset === 'openQuotes' &&
                                    t('stats__open_quotes', { ns: 'customer-detail' })}
                                {preset === 'openOrders' &&
                                    t('stats__open_orders', { ns: 'customer-detail' })}
                            </>
                        ) : (
                            <span>
                                {preset === 'openQuotes' && (
                                    <Trans
                                        t={t}
                                        i18nKey="presets__open_quotes_count"
                                        count={count}
                                        components={{ count: countElement }}
                                    />
                                )}
                                {preset === 'openOrders' && (
                                    <Trans
                                        t={t}
                                        i18nKey="presets__open_orders_count"
                                        count={count}
                                        components={{ count: countElement }}
                                    />
                                )}
                            </span>
                        )}
                    </ToggleButton>
                );
            })}
        </div>
    );
}
